import { storage } from '../../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

/**
 * 画像をリサイズ・圧縮する
 * @param {string} base64Data - base64画像データ
 * @param {string} imageFormat - 画像形式 (png, jpeg, etc.)
 * @param {number} maxWidth - 最大幅（デフォルト: 720px）
 * @param {number} quality - JPEG品質（0-1、デフォルト: 0.8）
 * @returns {Promise<Blob>} - 圧縮された画像のBlob
 */
const compressImage = async (
  base64Data,
  imageFormat,
  maxWidth = 720,
  quality = 0.8
) => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      // アスペクト比を保持してリサイズ
      let { width, height } = img
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }

      canvas.width = width
      canvas.height = height

      // 画像を描画
      ctx.drawImage(img, 0, 0, width, height)

      // Blobに変換
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('画像の圧縮に失敗しました'))
          }
        },
        `image/${imageFormat}`,
        quality
      )
    }

    img.onerror = () => {
      reject(new Error('画像の読み込みに失敗しました'))
    }

    img.src = `data:image/${imageFormat};base64,${base64Data}`
  })
}

/**
 * HTMLコンテンツ内のbase64画像を抽出し、Firebase Storageにアップロードして、URLに置き換える
 * @param {string} htmlContent - 処理対象のHTMLコンテンツ
 * @param {string} articleId - 記事ID（Storageのパスに使用）
 * @param {number} maxWidth - 画像の最大幅（デフォルト: 720px）
 * @param {number} quality - JPEG品質（0-1、デフォルト: 0.8）
 * @returns {Promise<string>} - base64をURLに置き換えた後のHTMLコンテンツ
 */
export const uploadBase64ImagesAndReplaceUrls = async (
  htmlContent,
  articleId,
  maxWidth = 720,
  quality = 0.8
) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent
  }

  // base64画像を含むimgタグを検出する正規表現
  const base64ImageRegex =
    /<img[^>]+src="data:image\/([^;]+);base64,([^"]+)"([^>]*)>/g

  let modifiedContent = htmlContent
  const matches = [...htmlContent.matchAll(base64ImageRegex)]

  // 各base64画像を処理
  for (const match of matches) {
    const fullImgTag = match[0] // マッチした完全なimgタグ
    const imageFormat = match[1] // 画像形式 (png, jpeg, etc.)
    const base64Data = match[2] // base64データ
    const additionalAttributes = match[3] || '' // その他の属性

    try {
      // 画像を圧縮・リサイズ
      const compressedBlob = await compressImage(
        base64Data,
        imageFormat,
        maxWidth,
        quality
      )

      // ファイルサイズをログ出力（デバッグ用）
      const originalSize = Math.round((base64Data.length * 3) / 4) // base64からバイト数に変換
      const compressedSize = compressedBlob.size
      const compressionRatio = Math.round(
        (1 - compressedSize / originalSize) * 100
      )

      console.log(
        `画像圧縮: ${Math.round(originalSize / 1024)}KB → ${Math.round(
          compressedSize / 1024
        )}KB (${compressionRatio}%削減)`
      )

      // ユニークなファイル名を生成
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substr(2, 9)
      const fileName = `${timestamp}_${randomId}.${imageFormat}`

      // Firebase Storageにアップロード
      const storageRef = ref(storage, `notices/images/${articleId}/${fileName}`)
      await uploadBytes(storageRef, compressedBlob)

      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(storageRef)

      // 元のimgタグをURLを含む新しいimgタグに置き換え
      // レスポンシブ対応のCSSクラスを追加
      const responsiveAttributes = additionalAttributes.includes('style')
        ? additionalAttributes
        : `${additionalAttributes} style="max-width: 100%; height: auto;"`
      const newImgTag = `<img src="${downloadURL}"${responsiveAttributes}>`
      modifiedContent = modifiedContent.replace(fullImgTag, newImgTag)

      console.log(`画像をアップロードしました: ${fileName}`)
    } catch (error) {
      console.error('画像のアップロード中にエラーが発生しました:', error)
      // エラーが発生しても処理を継続（他の画像の処理を続ける）
    }
  }

  return modifiedContent
}
